import React, { useState, useEffect } from 'react';
import { ProjectProvider } from './context/ProjectContext.tsx';
import { FilterProvider } from './context/FilterContext.tsx';
import { FilterControls } from './components/FilterControls.tsx';
import './App.css';
import { CategoryMergeTools } from './components/CategoryMergeTools.tsx';
import { FileUpload } from './components/FileUpload.tsx';
import { FilterHistoryPanel } from './components/FilterHistoryPanel.tsx';
import { LiteratureList } from './components/LiteratureList.tsx';
import { ProcessSection } from './components/ProcessSection.tsx';
import { ProjectSelection } from './components/ProjectSelection.tsx';
import { LiteratureSummarize } from './components/LiteratureSummarize.tsx';
import { ReviewerProvider } from './context/ReviewerContext.tsx';
import { Init } from './components/Init.tsx';
import { AuthProvider, useAuth } from './context/AuthContext.tsx';
import { AuthPage } from './components/AuthPage.tsx';
import { OptionsPage } from './components/OptionsPage.tsx';
import { ReviewImport } from './components/ReviewImport.tsx';
import { FillDOIs } from './components/FillDOIs.tsx';
import { AddFullText } from './components/AddFullText.tsx';
import { FindDuplicates } from './components/FindDuplicates.tsx';

type ThemeMode = 'light' | 'dark' | 'auto';

// Create an AppContent component to use the auth context
const AppContent = () => {
    const { isAuthenticated, user, logout } = useAuth();
    const [theme, setTheme] = useState<ThemeMode>('light');
    const [currentProjectName, setCurrentProjectName] = useState('');
    const [activeTab, setActiveTab] = useState('definition');

    useEffect(() => {
        // Handle auto theme based on system preference
        if (theme === 'auto') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
        } else {
            document.documentElement.setAttribute('data-theme', theme);
        }
    }, [theme]);

    const handleProjectChange = (projectId: number | null, projectName: string) => {
        setCurrentProjectName(projectName);
    };

    if (!isAuthenticated) {
        return <AuthPage />;
    }

    return (
        <ProjectProvider>
            <ReviewerProvider>
                <FilterProvider>
                    <Init />
                    <div className="app-container">
                        <div className="header-container">
                            <h1>
                                Literature Review Assistant {currentProjectName && `(${currentProjectName})`}
                            </h1>
                            <div className="user-controls">
                                <span className="user-info">Welcome, {user?.username}</span>
                                <button onClick={logout} className="logout-button">
                                    Logout
                                </button>
                                <select
                                    value={theme}
                                    onChange={(e) => setTheme(e.target.value as ThemeMode)}
                                    className="theme-select">
                                    <option value="light">Light</option>
                                    <option value="dark">Dark</option>
                                    <option value="auto">Auto</option>
                                </select>
                            </div>
                        </div>
                        <div className="tabs">
                            <button
                                className={`tab-button ${activeTab === 'definition' ? 'active' : ''}`}
                                onClick={() => setActiveTab('definition')}>
                                Definition
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'source' ? 'active' : ''}`}
                                onClick={() => setActiveTab('source')}
                                disabled={!currentProjectName}>
                                Source
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'research' ? 'active' : ''}`}
                                onClick={() => setActiveTab('research')}
                                disabled={!currentProjectName}>
                                Relevant Abstracts
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'fill-dois' ? 'active' : ''}`}
                                onClick={() => setActiveTab('fill-dois')}
                                disabled={!currentProjectName}>
                                Fill DOIs
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'add-fulltext' ? 'active' : ''}`}
                                onClick={() => setActiveTab('add-fulltext')}
                                disabled={!currentProjectName}>
                                Add Full-Text
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'find-duplicates' ? 'active' : ''}`}
                                onClick={() => setActiveTab('find-duplicates')}
                                disabled={!currentProjectName}>
                                Find Duplicates
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'import-reviews' ? 'active' : ''}`}
                                onClick={() => setActiveTab('import-reviews')}
                                disabled={!currentProjectName}>
                                Import Reviews
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'manual-review' ? 'active' : ''}`}
                                onClick={() => setActiveTab('manual-review')}
                                disabled={!currentProjectName}>
                                Manual Review
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'review' ? 'active' : ''}`}
                                onClick={() => setActiveTab('review')}
                                disabled={!currentProjectName}>
                                Review
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'summarize' ? 'active' : ''}`}
                                onClick={() => setActiveTab('summarize')}
                                disabled={!currentProjectName}>
                                Summarize
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'options' ? 'active' : ''}`}
                                onClick={() => setActiveTab('options')}>
                                Options
                            </button>
                        </div>
                        {activeTab === 'definition' && (
                            <section className="tab-content">
                                <ProjectSelection onProjectChange={handleProjectChange} />
                            </section>
                        )}

                        {activeTab === 'source' && (
                            <section className="tab-content">
                                <FileUpload />
                            </section>
                        )}

                        {activeTab === 'fill-dois' && (
                            <section className="tab-content">
                                <FillDOIs />
                            </section>
                        )}

                        {activeTab === 'add-fulltext' && (
                            <section className="tab-content">
                                <AddFullText />
                            </section>
                        )}

                        {activeTab === 'find-duplicates' && (
                            <section className="tab-content">
                                <FindDuplicates />
                            </section>
                        )}

                        {activeTab === 'research' && (
                            <section className="tab-content">
                                <ProcessSection />
                                <FilterControls />
                                <LiteratureList />
                            </section>
                        )}
                        {activeTab === 'import-reviews' && (
                            <section className="tab-content">
                                <ReviewImport />
                            </section>
                        )}
                        {activeTab === 'review' && (
                            <section className="tab-content">
                                {/* <CategoryMergeTools /> */}
                                <FilterHistoryPanel />
                                <FilterControls />
                                <LiteratureList />
                            </section>
                        )}

                        {activeTab === 'manual-review' && (
                            <section className="tab-content">
                                <FilterControls />
                                <LiteratureList review={true} />
                                {/* <ManualReview /> */}
                            </section>
                        )}

                        {activeTab === 'summarize' && (
                            <section className="tab-content">
                                <FilterControls />
                                <LiteratureSummarize />
                            </section>
                        )}
                        {activeTab === 'options' && (
                            <section className="tab-content">
                                <OptionsPage />
                            </section>
                        )}
                    </div>
                </FilterProvider>
            </ReviewerProvider>
        </ProjectProvider>
    );
};

// Main App component with AuthProvider
export default function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}
